import { clockStoppage, formatEventMinute } from "@livcg/schema";
import { useState } from "react";
import { Button, ModalShell } from "../ui/index.js";

export type EditPlayer = { id: string; firstName: string; lastName: string; number: number | null };
export type EditPatch = {
  minute: number;
  second: number;
  playerId: string;
  /** The event's second player — in a substitution, the one coming ON (`playerId`
   *  is the one going off). Sent only for substitutions; for other types it stays
   *  `undefined`, so the backend leaves the existing value untouched. */
  assistPlayerId?: string | null;
  added?: number;
  type?: string;
};

/** Modal for correcting a logged goal/card's time (if recorded late/wrong) and
 *  its player (wrong owner) — shared by EventLog (Control Room) and TabLog
 *  (classic). Team/type do not change (the score is not touched).
 *  A substitution involves two players, hence two pickers there. */
export function EditEventModal({
  initial,
  players,
  teamShort,
  typeLabel,
  eventType,
  onClose,
  onSave,
}: {
  initial: EditPatch;
  players: EditPlayer[];
  teamShort: string;
  typeLabel: string;
  eventType: string;
  onClose: () => void;
  onSave: (patch: EditPatch) => void;
}) {
  const [minute, setMinute] = useState(initial.minute);
  const [second, setSecond] = useState(initial.second);
  const [playerId, setPlayerId] = useState(initial.playerId);
  const [assistPlayerId, setAssistPlayerId] = useState(initial.assistPlayerId ?? "");
  const clamp = (v: number, max: number) => Math.max(0, Math.min(max, Math.floor(v || 0)));

  // Substitution: `playerId` — the player going off, `assistPlayerId` — the one coming on
  // (useQuickEventChain.fireSubGroup writes them in this order).
  const isSub = eventType === "substitution";
  const sortedPlayers = players.slice().sort((a, b) => (a.number ?? 999) - (b.number ?? 999));
  // `id` is required: the select is not directly inside the `<label>`, so the
  // association goes through htmlFor.
  const playerSelect = (
    id: string,
    value: string,
    onPick: (id: string) => void,
    placeholder: string,
  ) => (
    <select
      id={id}
      value={value}
      onChange={(e) => onPick(e.target.value)}
      className="min-w-0 flex-1 rounded border border-border bg-surface px-2 py-1 text-ink"
    >
      <option value="" disabled>{teamShort} — {placeholder}</option>
      {sortedPlayers.map((p) => (
        <option key={p.id} value={p.id}>
          {p.number ? `#${p.number} ` : ""}{p.firstName} {p.lastName}
        </option>
      ))}
    </select>
  );

  const sameBoth = isSub && playerId !== "" && playerId === assistPlayerId;
  const canSave = playerId !== "" && (!isSub || (assistPlayerId !== "" && !sameBoth));

  // The type can be switched within one "family" (if the operator recorded it wrong).
  // The effect on the score is recalculated on the backend, so even turning a
  // penalty goal into a missed one is safe.
  const GOAL_FAMILY = ["goal", "penalty_goal", "own_goal", "penalty_missed"];
  const CARD_FAMILY = ["yellow_card", "second_yellow", "red_card"];
  // The penalty shootout does not count toward the main score — a separate family.
  const SHOOTOUT_FAMILY = ["shootout_goal", "shootout_missed"];
  const family = GOAL_FAMILY.includes(eventType)
    ? GOAL_FAMILY
    : CARD_FAMILY.includes(eventType)
      ? CARD_FAMILY
      : SHOOTOUT_FAMILY.includes(eventType)
        ? SHOOTOUT_FAMILY
        : [];
  const TYPE_LABELS: Record<string, string> = {
    goal: "Oddiy gol",
    penalty_goal: "Penalti",
    own_goal: "Avtogol",
    penalty_missed: "Penalti o'tkazib yuborildi",
    yellow_card: "Sariq",
    second_yellow: "2-sariq",
    red_card: "Qizil",
    shootout_goal: "Seriya — gol",
    shootout_missed: "Seriya — o'tkazib yuborildi",
  };
  // Stoppage is a flag here: the "+N" comes from the clock time (47:21 → 45+3),
  // not typed — a typed board value is how "47+5" got on air. A base-form
  // event (45:00 +2, as PFL stores it) keeps its N while its time is untouched.
  const [stoppage, setStoppage] = useState((initial.added ?? 0) > 0);
  const untouched = minute === initial.minute && second === initial.second;
  const added = stoppage
    ? (clockStoppage(minute, second) ?? (untouched ? (initial.added ?? 0) : 0))
    : 0;
  const [type, setType] = useState(eventType);

  return (
    <ModalShell onClose={onClose} className="max-w-xs">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-[13px] font-semibold text-ink">
            Hodisani tahrirlash · <span className="text-ink-muted">{typeLabel}</span>
          </h3>
          <Button variant="ghost" size="xs" onClick={onClose}>✕</Button>
        </div>

        <label className="mb-2 flex items-center gap-2 text-[12px] text-ink-soft">
          <span className="w-16 shrink-0 text-ink-muted">Vaqt</span>
          <input
            type="number"
            min={0}
            max={130}
            value={minute}
            onChange={(e) => setMinute(clamp(e.target.valueAsNumber, 130))}
            className="w-16 rounded border border-border bg-surface px-2 py-1 text-ink"
          />
          <span className="text-ink-faint">'</span>
          <input
            type="number"
            min={0}
            max={59}
            value={second}
            onChange={(e) => setSecond(clamp(e.target.valueAsNumber, 59))}
            className="w-16 rounded border border-border bg-surface px-2 py-1 text-ink"
          />
        </label>
        <div className="mb-2 flex items-center gap-2 pl-[72px] text-[12px] text-ink-soft">
          <label className="flex items-center gap-1.5">
            <input
              type="checkbox"
              checked={stoppage}
              onChange={(e) => setStoppage(e.target.checked)}
            />
            Qo'shilgan vaqt
          </label>
          <span className="ml-auto font-mono text-ink">
            = {formatEventMinute(minute, second, added)}ʼ
          </span>
        </div>
        {stoppage && added === 0 && (
          <p className="mb-2 pl-[72px] text-[11px] text-warning">
            Vaqt taym oxiridan (45/90/105/120) o'tmagan — oddiy daqiqa bo'lib saqlanadi.
          </p>
        )}

        <label
          htmlFor="edit-event-player"
          className={`${isSub ? "mb-2" : "mb-3"} flex items-center gap-2 text-[12px] text-ink-soft`}
        >
          <span className="w-16 shrink-0 text-ink-muted">{isSub ? "Chiqdi" : "O'yinchi"}</span>
          {playerSelect(
            "edit-event-player",
            playerId,
            setPlayerId,
            isSub ? "chiqqan o'yinchi" : "o'yinchi",
          )}
        </label>

        {isSub && (
          <>
            <label
              htmlFor="edit-event-assist"
              className={`${sameBoth ? "mb-1" : "mb-3"} flex items-center gap-2 text-[12px] text-ink-soft`}
            >
              <span className="w-16 shrink-0 text-ink-muted">Kirdi</span>
              {playerSelect(
                "edit-event-assist",
                assistPlayerId,
                setAssistPlayerId,
                "kirgan o'yinchi",
              )}
            </label>
            {sameBoth && (
              <p className="mb-3 pl-[72px] text-[11px] text-danger-strong">
                Chiqqan va kirgan o'yinchi bir xil bo'lishi mumkin emas.
              </p>
            )}
          </>
        )}

        {family.length > 0 && (
          <label className="mb-3 flex items-center gap-2 text-[12px] text-ink-soft">
            <span className="w-16 shrink-0 text-ink-muted">Tur</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="min-w-0 flex-1 rounded border border-border bg-surface px-2 py-1 text-ink"
            >
              {family.map((t) => (
                <option key={t} value={t}>{TYPE_LABELS[t] ?? t}</option>
              ))}
            </select>
          </label>
        )}

        <div className="grid grid-cols-2 gap-1.5">
          <Button
            variant="success"
            size="sm"
            className="w-full uppercase"
            disabled={!canSave}
            onClick={() =>
              onSave({
                minute,
                second,
                playerId,
                ...(isSub ? { assistPlayerId } : {}),
                added: added > 0 ? added : 0,
                type,
              })
            }
          >
            Saqlash
          </Button>
          <Button variant="secondary" size="sm" className="w-full" onClick={onClose}>
            Bekor
          </Button>
        </div>
    </ModalShell>
  );
}
